import React from "react";
import "../../../css/Customer/Pages/CustomerHomePage/CustomerHomePage.css";
import NavBar from "frontend/Components/DesignSystem/NavBar";
import ButtonGreen from "frontend/Components/DesignSystem/Buttons/ButtonGreen";
import { useNavigate } from "react-router-dom";
import ButtonYellow from "frontend/Components/DesignSystem/Buttons/ButtonYellow";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as MortagePlanningIcon } from "../../../assets/icons/Mortgage-Planning.svg";
import { ReactComponent as CorporatePlanningIcon } from "../../../assets/icons/Corporate-Planning.svg";
import { ReactComponent as InvestmentPlanningIcon } from "../../../assets/icons/Investment-Planning.svg";
import { ReactComponent as RetirementPlanningIcon } from "../../../assets/icons/Retirement-Planning.svg";
import { ReactComponent as EquityReleaseIcon } from "../../../assets/icons/Equity -Release.svg";
import { ReactComponent as InsuranceProtectionIcon } from "../../../assets/icons/Insurance-Protection.svg";
import FinancialPerformanceDetails from "frontend/Components/FinancialPerformanceDocuments/FinancialPerformanceDetails";
import { ReactComponent as NumberOneIcon } from "../../../assets/icons/Number 1.svg";
import { ReactComponent as NumberTwoIcon } from "../../../assets/icons/Number 2.svg";
import { ReactComponent as NumberThreeIcon } from "../../../assets/icons/Number 3.svg";
import { ReactComponent as NumberFourIcon } from "../../../assets/icons/Number 4.svg";
import AdviserValues from "../../Components/AdviserValues/AdviserValue";
import FAQContainer from "frontend/Components/DesignSystem/FAQ/FAQContainer";
import Testimonials from "../../Components/Testimonials/Testimonials";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, SEO_SITE, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";

/**
 * CustomerHomePage component renders the main landing page for customers.
 * 
 * Features:
 * - Displays navigation bar with links to main sections.
 * - Shows a hero section with company branding and a call-to-action button.
 * - Highlights reasons to choose Rosemount and provides a "Learn More" button.
 * - Lists available financial services as clickable cards.
 * - Presents key attributes and adviser values.
 * - Includes a FAQ section addressing common customer questions.
 * - Displays testimonials from clients.
 * - Provides a contact form for customer inquiries.
 * - Renders the site footer.
 * 
 * @component
 * @returns {JSX.Element} The rendered customer home page.
 */
const CustomerHomePage = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/" },
    { label: "Our Services", navigateTo: "/OurServices" },
    { label: "About Us", navigateTo: "/AboutUs" },
    { label: "Contact Us", navigateTo: "/ContactUs" },
  ];

  const navigate = useNavigate();
  const faqs = [
    {
      question: "Are you an independent financial adviser?",
      answer:
        "Yes, we are a fully independent network of financial advisers and mortgage advisers.  Unlike many firms, we have no restrictions as to which providers we can use.  This means we can recommend you the best product that exists in the market, to ensure your individual needs are met. ",
    },
    {
      question: "How can I book an initial meeting?",
      answer:
        "Via the Contact Us form, if you fill this out you will get a response to set up initial contact from one of our advisers to start your journey with Rosemount.",
    },
    {
      question: "Where will I meet the adviser?",
      answer:
        "We can be flexible based on your requirements.  You can meet in person, or over the phone if you prefer.   ",
    },
    {
      question: "What advice can you offer?",
      answer:
        "This is wide ranging, from advice on mortgages and life assurance, through to advice on pensions and investments.  You can have an initial consultation to establish your needs, and to discuss how we can help.     ",
    },
    {
      question: "What fees are involved?",
      answer:
        "We offer an initial free consultation. After this our advisers will talk you through the next stages of your plan and the fees involved.",
    },
    {
      question: "When should I get an adviser?",
      answer:
        "There are several different stages in life to speak to an adviser about specific needs. We suggest the earlier to get an adviser the better and to talk about long term goals.",
    },
    {
      question: "What are the benefits of having an adviser?",
      answer:
        "It is invaluable to have somebody with experience helping, when we make those big financial decisions in life.  This can prevent costly mistakes from being made.  The adviser will also have experience of the market, and of the best products available.    ",
    },
    {
      question: "Which adviser is best for me?",
      answer:
        "Our advisers cover a wide range of fields. Once we’ve had an initial consultation, we can decide who  is best suited to help you on your journey. ",
    },
    {
      question: "What are the services you offer?",
      answer:
        "We offer advisers with much experience in mortgage planning, retirement planning, equity release, corporate planning, insurance advice and investment planning.",
    },
  ];

  const values = [
    {
      icon: <NumberOneIcon />,
      text: "You are our most important person",
    },
    {
      icon: <NumberTwoIcon />,
      text: "Recommendations are planned out and delivered to you",
    },
    {
      icon: <NumberThreeIcon />,
      text: "Deliver the best outcomes for you",
    },
    {
      icon: <NumberFourIcon />,
      text: "We talk you through every step of the process",
    },
  ];

  const aboutPageStructuredData = {
    "@type": "AboutPage",
    name: SEO_PAGES.customerAboutUs.title,
    description: SEO_PAGES.customerAboutUs.description,
    url: getPageUrl("/AboutUs"),
    about: {
      "@id": `${SEO_SITE.siteUrl}/#organization`,
    },
    mainEntity: {
      "@id": `${SEO_SITE.siteUrl}/#organization`,
    },
  };

  const financialServiceStructuredData = {
    "@type": "FinancialService",
    "@id": `${SEO_SITE.siteUrl}/#financialservice`,
    name: SEO_SITE.siteName,
    url: SEO_SITE.siteUrl,
    description:
      "Rosemount Financial Solutions is a full financial planning firm with nationwide coverage, helping customers build bespoke plans for their financial goals.",
    areaServed: "United Kingdom",
    foundingDate: "2004",
    serviceType: [
      "Financial Planning",
      "Mortgage Advice",
      "Protection Advice",
      "Independent Financial Advice",
    ],
  };
  return (
    <div className="Customer-Home-Page">
      <SeoHelmet
        page={SEO_PAGES.customerAboutUs}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "About Us",
            url: getPageUrl("/AboutUs"),
          },
        ]}
        extraSchemas={[aboutPageStructuredData, financialServiceStructuredData]}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["customer-home"]}
        altText="A couple sat on the sofa together."
        children={
          <>
            <h1>Rosemount Financial Solutions (IFA) Ltd</h1>
            <div className="ButtonGreen">
              <ButtonGreen
                buttonText="Adviser Page"
                onClick={() => navigate("/Adviser")}
              />
            </div>
          </>
        }
      />

      <div className="Why-Rosemount">
        <h2>Why Rosemount?</h2>
        <div className="SubTitle">
          We are a full financial planning firm with nationwide coverage. Our
          advisers grow meaningful relationships to build bespoke plans for your
          goals
        </div>
        <ButtonYellow
          buttonText="Learn More"
          onClick={() => {
            navigate("/AboutUs");
          }}
        />
      </div>
      <div className="Customer-Services-Container">
        <h2>Our Services</h2>
        <div className="Customer-Services">
          <CardProduct
            Icon={MortagePlanningIcon}
            title="Mortgage Planning"
            link="/OurServices/MortgagePlanning"
          />
          <CardProduct
            Icon={CorporatePlanningIcon}
            title="Corporate Planning"
            link="/OurServices/CorporatePlanning"
          />
          <CardProduct
            Icon={InvestmentPlanningIcon}
            title="Investment Planning"
            link="/OurServices/InvestmentPlanning"
          />
          <CardProduct
            Icon={RetirementPlanningIcon}
            title="Retirement Planning"
            link="/OurServices/RetirementPlanning"
          />
          <CardProduct
            Icon={EquityReleaseIcon}
            title="Equity Release"
            link="/OurServices/EquityRelease"
          />
          <CardProduct
            Icon={InsuranceProtectionIcon}
            title="Insurance/ Protection"
            link="/OurServices/InsuranceProtection"
          />
        </div>
      </div>
      <FinancialPerformanceDetails title="Key Attributes" />
      <AdviserValues values={values} title="Our Advisers Values" />
      <FAQContainer faqs={faqs} />
      <Testimonials />
      <ContactUs isAdviser={false} />
      <Footer />
    </div>
  );
};

export default CustomerHomePage;
